import { createEntityAdapter, EntityState } from '@ngrx/entity';
import { Action, createReducer, on } from '@ngrx/store';

import { auditLogAdapter } from '../shared-audit.adapters';
import * as actions from './training-requirement.actions';
import {
  TrainingRequirementSkeleton,
  TrainingRequirementAudit,
  WarningRuleAudit,
  BusinessPersonTrainingRequirementAudit,
  BusinessPersonWithTrainingSkeleton,
  TrainingGroupAudit,
  TrainingGroupSkeleton,
  TrainingGroupTrainingRequirementAudit
} from '../../+models/training-requirement-audit.model';

// States
interface TrainingRequirementSkeletonState extends EntityState<TrainingRequirementSkeleton> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: any;
  loaded: boolean;
  loading: boolean;
}

interface TrainingGroupSkeletonState extends EntityState<TrainingGroupSkeleton> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: any;
  loaded: boolean;
  loading: boolean;
}

interface BusinessPersonWithTrainingSkeletonState
  extends EntityState<BusinessPersonWithTrainingSkeleton> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: any;
  loaded: boolean;
  loading: boolean;
}

interface TrainingRequirementAuditState extends EntityState<TrainingRequirementAudit> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: any;
  loaded: boolean;
  loading: boolean;
  selectedId: number | null;
  pageNumber: number;
  lastAuditLogId: number | null;
}

interface TrainingGroupAuditState extends EntityState<TrainingGroupAudit> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: any;
  loaded: boolean;
  loading: boolean;
  selectedId: number | null;
  pageNumber: number;
  lastAuditLogId: number | null;
}

interface TrainingGroupTrainingRequirementAuditState
  extends EntityState<TrainingGroupTrainingRequirementAudit> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: any;
  loaded: boolean;
  loading: boolean;
  selectedId: number | null;
  pageNumber: number;
  lastAuditLogId: number | null;
}

interface WarningRuleAuditState extends EntityState<WarningRuleAudit> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: any;
  loaded: boolean;
  loading: boolean;
  selectedId: number | null;
  pageNumber: number;
  lastAuditLogId: number | null;
}

interface BusinessPersonTrainingRequirementAuditState
  extends EntityState<BusinessPersonTrainingRequirementAudit> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: any;
  loaded: boolean;
  loading: boolean;
  selectedId: number | null;
  pageNumber: number;
  lastAuditLogId: number | null;
}

// Adapters
const trainingRequirementSkeletonAdapter = createEntityAdapter<TrainingRequirementSkeleton>({
  selectId: p => p.trainingRequirementId
});

const trainingGroupSkeletonAdapter = createEntityAdapter<TrainingGroupSkeleton>({
  selectId: p => p.trainingGroupId
});

const businessPersonWithTrainingSkeletonAdapter =
  createEntityAdapter<BusinessPersonWithTrainingSkeleton>({
    selectId: p => p.businessPersonId
  });

// Entity Selectors
export const trainingRequirementSkeletonSelectors =
  trainingRequirementSkeletonAdapter.getSelectors();
export const trainingGroupSkeletonSelectors = trainingGroupSkeletonAdapter.getSelectors();
export const businessPersonWithTrainingSkeletonSelectors =
  businessPersonWithTrainingSkeletonAdapter.getSelectors();
export const trainingRequirementAuditSelectors = auditLogAdapter.getSelectors();
export const warningRuleAuditSelectors = auditLogAdapter.getSelectors();
export const businessPersonTrainingRequirementAuditSelectors = auditLogAdapter.getSelectors();
export const trainingGroupAuditSelectors = auditLogAdapter.getSelectors();
export const trainingGroupTrainingRequirementAuditSelectors = auditLogAdapter.getSelectors();

// Initial States
const trainingRequirementSkeletonInitialState = trainingRequirementSkeletonAdapter.getInitialState({
  error: null,
  loaded: false,
  loading: false
});

const trainingGroupSkeletonInitialState = trainingGroupSkeletonAdapter.getInitialState({
  error: null,
  loaded: false,
  loading: false
});

const businessPersonWithTrainingSkeletonInitialState =
  businessPersonWithTrainingSkeletonAdapter.getInitialState({
    error: null,
    loaded: false,
    loading: false
  });

const trainingRequirementAuditInitialState = auditLogAdapter.getInitialState({
  error: null,
  loaded: false,
  loading: false,
  selectedId: null,
  pageNumber: 1,
  lastAuditLogId: null
});
const trainingGroupAuditInitialState = auditLogAdapter.getInitialState({
  error: null,
  loaded: false,
  loading: false,
  selectedId: null,
  pageNumber: 1,
  lastAuditLogId: null
});
const trainingGroupTrainingRequirementAuditInitialState = auditLogAdapter.getInitialState({
  error: null,
  loaded: false,
  loading: false,
  selectedId: null,
  pageNumber: 1,
  lastAuditLogId: null
});
const warningRuleAuditInitialState = auditLogAdapter.getInitialState({
  error: null,
  loaded: false,
  loading: false,
  selectedId: null,
  pageNumber: 1,
  lastAuditLogId: null
});
const businessPersonTrainingRequirementAuditInitialState = auditLogAdapter.getInitialState({
  error: null,
  loaded: false,
  loading: false,
  selectedId: null,
  pageNumber: 1,
  lastAuditLogId: null
});

export interface State {
  trainingRequirementSkeletons: TrainingRequirementSkeletonState;
  businessPersonWithTrainingSkeletons: BusinessPersonWithTrainingSkeletonState;
  trainingRequirementAudit: TrainingRequirementAuditState;
  warningRuleAudit: WarningRuleAuditState;
  businessPersonTrainingRequirementAudit: BusinessPersonTrainingRequirementAuditState;
  trainingGroupAudit: TrainingGroupAuditState;
  trainingGroupSkeletons: TrainingGroupSkeletonState;
  trainingGroupTrainingRequirementAudit: TrainingGroupTrainingRequirementAuditState;
}

const initialState: State = {
  trainingRequirementSkeletons: trainingRequirementSkeletonInitialState,
  businessPersonWithTrainingSkeletons: businessPersonWithTrainingSkeletonInitialState,
  trainingRequirementAudit: trainingRequirementAuditInitialState,
  warningRuleAudit: warningRuleAuditInitialState,
  businessPersonTrainingRequirementAudit: businessPersonTrainingRequirementAuditInitialState,
  trainingGroupSkeletons: trainingGroupSkeletonInitialState,
  trainingGroupAudit: trainingGroupAuditInitialState,
  trainingGroupTrainingRequirementAudit: trainingGroupTrainingRequirementAuditInitialState
};

const trainingRequirementReducer = createReducer<State>(
  initialState,
  on(actions.LoadTrainingRequirementSkeletons, state => ({
    ...state,

    trainingRequirementAudit: {
      ...state.trainingRequirementAudit,
      loading: true
    }
  })),

  on(actions.TrainingRequirementSkeletonsLoaded, (state, action) => ({
    ...state,

    trainingRequirementSkeletons: {
      ...state.trainingRequirementSkeletons,
      ...trainingRequirementSkeletonAdapter.setAll(
        action.payload,
        state.trainingRequirementSkeletons
      ),
      loading: false,
      loaded: true
    }
  })),

  on(actions.LoadTrainingGroupSkeletons, state => ({
    ...state,

    trainingGroupAudit: {
      ...state.trainingGroupAudit,
      loading: true
    }
  })),

  on(actions.TrainingGroupSkeletonsLoaded, (state, action) => ({
    ...state,

    trainingGroupSkeletons: {
      ...state.trainingGroupSkeletons,
      ...trainingGroupSkeletonAdapter.setAll(action.payload, state.trainingGroupSkeletons),
      loading: false,
      loaded: true
    }
  })),

  on(actions.LoadBusinessPersonWithTrainingSkeletons, state => ({
    ...state,

    businessPersonWithTrainingSkeletons: {
      ...state.businessPersonWithTrainingSkeletons,
      loading: true
    }
  })),

  on(actions.BusinessPersonWithTrainingSkeletonsLoaded, (state, action) => ({
    ...state,

    businessPersonWithTrainingSkeletons: {
      ...state.businessPersonWithTrainingSkeletons,
      ...businessPersonWithTrainingSkeletonAdapter.setAll(
        action.payload,
        state.businessPersonWithTrainingSkeletons
      ),
      loading: false,
      loaded: true
    }
  })),

  on(actions.ResetTrainingRequirementAudit, state => ({
    ...state,

    trainingRequirementAudit: initialState.trainingRequirementAudit,
    businessPersonTrainingRequirementAudit: initialState.businessPersonTrainingRequirementAudit
  })),

  on(actions.LoadTrainingRequirementAudit, (state, action) => ({
    ...state,

    trainingRequirementAudit: {
      ...state.trainingRequirementAudit,
      lastAuditLogId:
        state.trainingRequirementAudit.selectedId === action.trainingRequirementId
          ? state.trainingRequirementAudit.lastAuditLogId
          : null,
      loading: true
    }
  })),

  on(actions.TrainingRequirementAuditLoaded, (state, action) => ({
    ...state,

    trainingRequirementAudit: {
      ...state.trainingRequirementAudit,
      ...(!state.trainingRequirementAudit.lastAuditLogId
        ? auditLogAdapter.setAll(action.audits.resultSet, state.trainingRequirementAudit)
        : auditLogAdapter.addMany(action.audits.resultSet, state.trainingRequirementAudit)),
      loaded: true,
      loading: false,
      selectedId: action.trainingRequirementId,
      pageNumber: action.audits.pageNumber,
      lastAuditLogId: Math.min(...action.audits.resultSet.map(r => +r.auditLogId))
    }
  })),

  on(actions.LoadTrainingGroupAudit, (state, action) => ({
    ...state,

    trainingGroupAudit: {
      ...state.trainingGroupAudit,
      lastAuditLogId:
        state.trainingGroupAudit.selectedId === action.trainingGroupId
          ? state.trainingGroupAudit.lastAuditLogId
          : null,
      loading: true
    }
  })),

  on(actions.TrainingGroupAuditLoaded, (state, action) => ({
    ...state,

    trainingGroupAudit: {
      ...state.trainingGroupAudit,
      ...(!state.trainingGroupAudit.lastAuditLogId
        ? auditLogAdapter.setAll(action.audits.resultSet, state.trainingGroupAudit)
        : auditLogAdapter.addMany(action.audits.resultSet, state.trainingGroupAudit)),
      loaded: true,
      loading: false,
      selectedId: action.trainingGroupId,
      pageNumber: action.audits.pageNumber,
      lastAuditLogId: Math.min(...action.audits.resultSet.map(r => +r.auditLogId))
    }
  })),

  on(actions.LoadBusinessPersonTrainingRequirementAudit, (state, action) => ({
    ...state,

    businessPersonTrainingRequirementAudit: {
      ...state.businessPersonTrainingRequirementAudit,
      lastAuditLogId:
        state.businessPersonTrainingRequirementAudit.selectedId ===
        action.businessPersonTrainingRequirementId
          ? state.businessPersonTrainingRequirementAudit.lastAuditLogId
          : null,
      loading: true
    }
  })),

  on(actions.BusinessPersonTrainingRequirementAuditLoaded, (state, action) => ({
    ...state,

    businessPersonTrainingRequirementAudit: {
      ...state.businessPersonTrainingRequirementAudit,
      ...(!state.businessPersonTrainingRequirementAudit.lastAuditLogId
        ? auditLogAdapter.setAll(
            action.audits.resultSet,
            state.businessPersonTrainingRequirementAudit
          )
        : auditLogAdapter.addMany(
            action.audits.resultSet,
            state.businessPersonTrainingRequirementAudit
          )),
      loaded: true,
      loading: false,
      selectedId: action.businessPersonTrainingRequirementId,
      pageNumber: action.audits.pageNumber,
      lastAuditLogId: Math.min(...action.audits.resultSet.map(r => +r.auditLogId))
    }
  })),

  on(actions.LoadWarningRuleAudit, (state, action) => ({
    ...state,

    warningRuleAudit: {
      ...state.warningRuleAudit,
      lastAuditLogId:
        state.warningRuleAudit.selectedId === action.warningRuleId
          ? state.warningRuleAudit.lastAuditLogId
          : null,
      loading: true
    }
  })),

  on(actions.WarningRuleAuditLoaded, (state, action) => ({
    ...state,

    warningRuleAudit: {
      ...state.warningRuleAudit,
      ...(!state.warningRuleAudit.lastAuditLogId
        ? auditLogAdapter.setAll(action.audits.resultSet, state.warningRuleAudit)
        : auditLogAdapter.addMany(action.audits.resultSet, state.warningRuleAudit)),
      loaded: true,
      loading: false,
      selectedId: action.warningRuleId,
      pageNumber: action.audits.pageNumber,
      lastAuditLogId: Math.min(...action.audits.resultSet.map(r => +r.auditLogId))
    }
  })),

  on(actions.LoadTrainingGroupTrainingRequirementAudit, (state, action) => ({
    ...state,

    trainingGroupTrainingRequirementAudit: {
      ...state.trainingGroupTrainingRequirementAudit,
      lastAuditLogId:
        state.trainingGroupTrainingRequirementAudit.selectedId ===
        action.trainingGroupTrainingRequirementId
          ? state.trainingGroupTrainingRequirementAudit.lastAuditLogId
          : null,
      loading: true
    }
  })),

  on(actions.TrainingGroupTrainingRequirementAuditLoaded, (state, action) => ({
    ...state,

    trainingGroupTrainingRequirementAudit: {
      ...state.trainingGroupTrainingRequirementAudit,
      ...(!state.trainingGroupTrainingRequirementAudit.lastAuditLogId
        ? auditLogAdapter.setAll(
            action.audits.resultSet,
            state.trainingGroupTrainingRequirementAudit
          )
        : auditLogAdapter.addMany(
            action.audits.resultSet,
            state.trainingGroupTrainingRequirementAudit
          )),
      loaded: true,
      loading: false,
      selectedId: action.trainingGroupTrainingRequirementId,
      pageNumber: action.audits.pageNumber,
      lastAuditLogId: Math.min(...action.audits.resultSet.map(r => +r.auditLogId))
    }
  }))
);

export function reducer(state: State | undefined, action: Action) {
  return trainingRequirementReducer(state, action);
}
